import React, { createContext, useContext, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import api from '../../../api'
import { useWorkspace } from '../WorkspaceContext'
import { useAppUser } from '../../../context/UserContext/UserContext'
import { codeOptions } from '../snippets/AcceptedLanguages'
import { isValidFile } from '../../../shared/utils/getFileIconAndExtensionHelper'
import { completeTransferNotice, showTransferNotice } from '../../../shared/components/TransferNotifications/TransferNotifications'
import { useFileLimitPopup } from './useFileLimitPopup'
import { MAX_ROOM_ITEMS, FOLDER_LIMIT, ROOM_STORAGE_LIMIT_BYTES, getFolderPaths, hasExceededFolderDepth } from './folderUploadLimits'

// The single upload engine used by every upload entry point in the
// workspace — the "Add New" file/folder picker (AddNewPopup.tsx) and
// workspace-wide drag-and-drop (useWorkspaceFileDropZone.ts). Both call the
// exact same beginUpload()/isSnip() here, so validation, upload limits, the
// Confirm Upload popup, duplicate-name handling and the actual S3/snippet
// upload calls only exist in one place. This is a straight extraction of what
// used to live inline in AddNewPopup.tsx — no behavior change for the picker.

export type UploadMode = 'file' | 'folder';

type BeginUploadCallbacks = {
    // Fired synchronously once beginUpload has passed every early-return
    // validation check and is either opening the Confirm Upload popup or
    // starting the upload directly — i.e. exactly the point at which the
    // picker used to hide itself. Not fired for the "unsupported file type" /
    // hard single-file-limit toast early-returns, which leave the picker
    // visible behind the toast.
    onEngaged?: () => void;
    // Fired once that engagement has fully settled (upload succeeded, failed,
    // or the Confirm Upload popup was cancelled).
    onSettled?: () => void;
};

type UploadEngineCtx = {
    beginUpload: (
        rawFiles: File[],
        mode: UploadMode,
        targetFolderId: string | null,
        callbacks?: BeginUploadCallbacks
    ) => Promise<void>;
    isUploading: boolean;
    isSnip: (f: File) => boolean;
    // Exposed so callers (AddNewPopup's own pre-flight "don't even open the
    // picker" checks) don't need a second useAppUser() call to compute this.
    hasOwnerBypass: boolean;
    // Folder widget (sidebar tree row) currently under the cursor during an
    // in-progress OS file drag in editor mode — null otherwise. Set by
    // useWorkspaceFileDropZone, read by FileExplorerTab to drive the same
    // drop-target highlight class used for internal drag-and-drop.
    hoveredDropFolderId: string | null;
    setHoveredDropFolderId: (id: string | null) => void;
};

const UploadEngineContext = createContext<UploadEngineCtx | null>(null);

export function useUploadEngine() {
    const ctx = useContext(UploadEngineContext);
    if (!ctx) throw new Error("useUploadEngine must be used within UploadEngineProvider");
    return ctx;
}

export const UploadEngineProvider = ({ children }: { children: React.ReactNode }) => {
    const { Snippets, Files, Folders, roomStorage, setLimitMessage, setLimitPopup } = useWorkspace();
    const { user: appUser } = useAppUser();
    const hasOwnerBypass = appUser?.plan === 'flashroom-owner';
    const { roomId } = useParams();
    const clientId = localStorage.getItem('flashroom_id');

    const [isUploading, setIsUploading] = useState(false);
    const uploadingRef = useRef(false);
    const [hoveredDropFolderId, setHoveredDropFolderId] = useState<string | null>(null);

    const { openFileLimitPopup, fileLimitPopupNode } = useFileLimitPopup();

    const isSnip = (f: File) => {
        const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
        return codeOptions.some(option => option.extension === ext) || ext === '.txt';
    };

    const broadcastUploadRefresh = async () => {
        if (!roomId) return;
        await api.post(`/rooms/${roomId}/broadcastFileUpload`);
    };

    const getFilesTotalSize = (files: File[]) => {
        return files.reduce((total, file) => total + file.size, 0);
    };

    const getTransferDisplayName = (selectedFiles: File[], mode: UploadMode) => {
        if (mode === "folder") {
            // A drag-and-drop folder-mode batch can include loose top-level
            // files (webkitRelativePath === '') alongside real folders, unlike
            // a native folder picker where every file shares a path — pick the
            // first entry that actually has one for the display name.
            const withPath = selectedFiles.find(f => f.webkitRelativePath);
            const firstPath = withPath?.webkitRelativePath;
            return firstPath?.split("/")[0] || selectedFiles[0]?.name || "Folder upload";
        }

        if (selectedFiles.length === 1) {
            return selectedFiles[0].name;
        }

        return `${selectedFiles.length} files`;
    };

    const getTransferItemType = (selectedFiles: File[], mode: UploadMode): "file" | "folder" | "snippet" => {
        if (mode === "folder") return "folder";
        if (selectedFiles.length === 1 && isSnip(selectedFiles[0])) return "snippet";

        return "file";
    };

    const uploadOne = async (f: File, fold?: string | null) => {
        if (!roomId || !clientId) throw new Error("missing ids");

        const initRes = await api.post(`/rooms/${roomId}/members/${clientId}/initupload`, {
            folderId: null,
            name: f.name,
            size: f.size,
            contentType: f.type,
            fileSetting: "room",
            folderParent: fold ? fold : null
        });

        const { fileId, uploadUrl } = initRes.data.file;

        const putRes = await fetch(uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": f.type || "application/octet-stream" },
            body: f,
        });
        if (!putRes.ok) throw new Error(`S3 upload failed for ${f.name}`);

        await api.patch(`/rooms/${roomId}/files/${fileId}/finishupload`, {
            status: "uploaded",
            etag: putRes.headers.get("etag") ?? undefined,
        });

        return fileId;
    };

    const uploadSnippet = async (f: File, fold?: string | null, silent?: boolean) => {
        const dotIndex = f.name.lastIndexOf(".");

        const ext = dotIndex !== -1 ? f.name.slice(dotIndex).toLowerCase() : "";
        const baseName = dotIndex !== -1 ? f.name.slice(0, dotIndex) : f.name;

        const code = codeOptions.find((c) => c.extension === ext);
        const lang = code?.cValue ?? "text";
        const isCode = codeOptions.some((c) => c.extension === ext);

        const content = await f.text();

        const payload = {
            snippetName: baseName,
            snippetType: isCode ? 'code' : 'text',
            snipText: content,
            snippetLanguage: isCode ? lang : null,
            snipPermissions: 'view',
            createdBy: clientId,
            snippetSetting: 'room',
            folderParent: fold ? fold : null,
            silent: silent ?? false,
            // Never fail an upload on a name collision — mirrors file uploads.
            autoRename: true,
        };

        await api.post(`/rooms/${roomId}/snippets`, payload);
    };

    const uploadFolderTree = async (selectedFiles: File[], baseFolderId: string | null) => {
        if (!roomId || !clientId) throw new Error("missing ids");

        if (hasExceededFolderDepth(selectedFiles)) {
            setLimitPopup(true);
            setLimitMessage("Maximum folder depth of 5 cannot be exceeded");
            return;
        }

        const folderPaths = getFolderPaths(selectedFiles);
        const folderMap: Record<string, string> = {};

        if (!hasOwnerBypass && Snippets.length + Files.length + selectedFiles.length > MAX_ROOM_ITEMS) {
            setLimitPopup(true);
            setLimitMessage('File limit reached');
            return;
        }

        if (!hasOwnerBypass && folderPaths.length + Folders.length > FOLDER_LIMIT) {
            setLimitPopup(true);
            setLimitMessage('Folder Limit Reached');
            return;
        }

        for (const path of folderPaths) {
            const parts = path.split("/");
            const folderName = parts[parts.length - 1];
            const parentPath = parts.slice(0, -1).join("/");
            const parentId = parentPath ? folderMap[parentPath] : baseFolderId;

            const res = await api.post(`/rooms/${roomId}/folder`, {
                fname: folderName,
                creatorId: clientId,
                folderParent: parentId,
                // Never fail a folder-tree upload on a name collision — mirrors
                // how file uploads already never fail on one.
                autoRename: true,
            });

            folderMap[path] = res.data.created._id;
        }

        for (const file of selectedFiles) {
            const parts = file.webkitRelativePath.split("/");
            parts.pop();
            const folderPath = parts.join("/");
            const folderId = folderMap[folderPath] ?? baseFolderId;

            if (isSnip(file)) {
                await uploadSnippet(file, folderId, true);
            } else {
                await uploadOne(file, folderId);
            }
        }

        await broadcastUploadRefresh();
    };

    const uploadMultiple = async (fil: File[], baseFolderId: string | null) => {
        if (!hasOwnerBypass && Snippets.length + Files.length + fil.length > MAX_ROOM_ITEMS) {
            setLimitPopup(true);
            setLimitMessage('File limit reached');
            return;
        }

        for (const f of fil) {
            if (isSnip(f)) {
                await uploadSnippet(f, baseFolderId, true);
            } else {
                await uploadOne(f, baseFolderId);
            }
        }

        await broadcastUploadRefresh();
    };

    const submitUpload = async (selectedFiles: File[], mode: UploadMode, targetFolderId: string | null) => {
        if (uploadingRef.current) return;
        let noticeId: number | null = null;
        let uploadFinished = false;

        try {
            uploadingRef.current = true;
            setIsUploading(true);
            noticeId = showTransferNotice({
                direction: "upload",
                itemType: getTransferItemType(selectedFiles, mode),
                name: getTransferDisplayName(selectedFiles, mode),
            });

            if (mode === "file") {
                await uploadMultiple(selectedFiles, targetFolderId);
            } else {
                await uploadFolderTree(selectedFiles, targetFolderId);
            }
            uploadFinished = true;
        } catch (error) {
            setLimitMessage("Upload failed. Please try again.");
            setLimitPopup(true);
        } finally {
            if (noticeId && uploadFinished) completeTransferNotice(noticeId);
            uploadingRef.current = false;
            setIsUploading(false);
        }
    };

    const beginUpload = async (
        rawFiles: File[],
        mode: UploadMode,
        targetFolderId: string | null,
        callbacks?: BeginUploadCallbacks
    ) => {
        if (uploadingRef.current) return;

        const acceptedFiles = rawFiles.filter(file => isValidFile(file.name));

        if (acceptedFiles.length === 0) {
            setLimitPopup(true);
            setLimitMessage(
                rawFiles.length === 1
                    ? "Unsupported file type"
                    : mode === "folder"
                        ? "No supported file types found in this folder"
                        : "None of the selected files are a supported file type"
            );
            return;
        }

        const uploadBytes = getFilesTotalSize(acceptedFiles);

        const exceedsStorage = !hasOwnerBypass && roomStorage + uploadBytes > ROOM_STORAGE_LIMIT_BYTES;
        const exceedsItemLimit = !hasOwnerBypass && Snippets.length + Files.length + acceptedFiles.length > MAX_ROOM_ITEMS;
        const exceedsFolderLimit = mode === "folder" && !hasOwnerBypass
            && getFolderPaths(acceptedFiles).length + Folders.length > FOLDER_LIMIT;

        // A batch of more than one file that trips a limit is worth letting the
        // user trim down themselves. A single oversized/over-limit file can't be
        // trimmed (removing it leaves nothing to upload), so that still falls
        // through to the regular limit toast below.
        if ((exceedsStorage || exceedsItemLimit || exceedsFolderLimit) && acceptedFiles.length > 1) {
            callbacks?.onEngaged?.();
            openFileLimitPopup({
                files: acceptedFiles,
                mode,
                onConfirm: async (remaining) => {
                    await submitUpload(remaining, mode, targetFolderId);
                    callbacks?.onSettled?.();
                },
                onCancel: () => {
                    callbacks?.onSettled?.();
                },
            });
            return;
        }

        if (exceedsStorage) {
            setLimitPopup(true);
            setLimitMessage("Storage Limit Exceeded");
            return;
        }

        if (exceedsItemLimit) {
            setLimitPopup(true);
            setLimitMessage('File limit reached');
            return;
        }

        if (mode === "folder") {
            if (hasExceededFolderDepth(acceptedFiles)) {
                setLimitPopup(true);
                setLimitMessage("Maximum folder depth of 5 cannot be exceeded");
                return;
            }

            if (exceedsFolderLimit) {
                setLimitPopup(true);
                setLimitMessage('Folder Limit Reached');
                return;
            }
        }

        callbacks?.onEngaged?.();
        await submitUpload(acceptedFiles, mode, targetFolderId);
        callbacks?.onSettled?.();
    };

    return (
        <UploadEngineContext.Provider value={{ beginUpload, isUploading, isSnip, hasOwnerBypass, hoveredDropFolderId, setHoveredDropFolderId }}>
            {children}
            {fileLimitPopupNode}
        </UploadEngineContext.Provider>
    );
};
